import { useState } from 'react';
import { useTheme } from '../../hooks/useTheme';
import Badge from '../shared/Badge';

/**
 * Theme Toggle Component
 *
 * Quick switcher between Dark, Light, and Custom themes
 * Shows current theme, admin enforcement status, and allows theme switching
 */
export default function ThemeToggle({ showLabel = true, onEditCustom = null }) {
  const {
    currentThemeType,
    switchTheme,
    isAdminEnforced,
    canSwitchTheme,
    themeTypes
  } = useTheme();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const themes = [
    { id: themeTypes.DARK, name: 'Dark', icon: '🌙', description: 'Dark mode' },
    { id: themeTypes.LIGHT, name: 'Light', icon: '☀️', description: 'Light mode' },
    { id: themeTypes.CUSTOM, name: 'Custom', icon: '🎨', description: 'Custom theme' }
  ];

  const currentTheme = themes.find(t => t.id === currentThemeType) || themes[0];

  const handleThemeSelect = async (themeType) => {
    await switchTheme(themeType);
    setIsDropdownOpen(false);
  };

  return (
    <div>
      {isAdminEnforced && (
        <Badge variant="warning" className="mb-3">
          Admin Enforced
        </Badge>
      )}

      <div className="flex gap-3">
        {themes.map(theme => (
          <button
            key={theme.id}
            onClick={() => handleThemeSelect(theme.id)}
            disabled={isAdminEnforced}
            className={`
              flex-1 flex flex-col items-center gap-2 px-6 py-4 rounded-xl
              border-2 transition-all duration-200
              ${currentThemeType === theme.id
                ? 'border-purple-500 bg-purple-600/20'
                : 'border-gray-700 bg-gray-800/50 hover:bg-gray-700/50 hover:border-gray-600'}
              ${isAdminEnforced ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            title={isAdminEnforced ? 'Theme is enforced by administrator' : `Switch to ${theme.name} mode`}
          >
            <span className="text-3xl">{theme.icon}</span>
            <span className="text-sm font-medium text-white">
              {theme.name}
            </span>
            {currentThemeType === theme.id && (
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
